import React, { useEffect, useRef } from "react";
import CompareWith from "./CompareWith";
import WellnessBlock from "./WellnessBlock";
import WellnessTrend from "./WellnessTrend";

const prefersReducedMotion = () => Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

/** The open/close transition (`duration-300`), plus a frame or two of slack. */
const OPEN_MS = 340;

/**
 * A collapsible detail section (heatmap or small trend) with its own
 * Compare-with switch.
 *
 * @param {Object} props
 * @param {Object} props.d - One entry of buildView().details.
 * @param {string} props.bodyKey - The active compare range; a new one remounts the body so its slide-in replays.
 * @param {string} props.bodyClass - The body's slide-in animation class, if any.
 * @param {() => void} props.onToggle
 * @param {(sectionId: string, rangeKey: string) => void} props.onRange
 */
const DetailSection = ({ d, bodyKey, bodyClass, onToggle, onRange }) => {
  const ref = useRef(null);
  const follow = useRef(0);
  const stopFollowing = () => {
    cancelAnimationFrame(follow.current);
    follow.current = 0;
  };
  useEffect(() => stopFollowing, []);

  // Opening: keep the section in view on every frame while it grows, so the
  // page moves with it rather than after it. `nearest` scrolls only as far as
  // needed; a section taller than the view lines its header up at the top.
  const toggle = () => {
    stopFollowing();
    if (!d.isOpen) {
      const until = performance.now() + (prefersReducedMotion() ? 0 : OPEN_MS);
      const step = () => {
        ref.current?.scrollIntoView({ block: "nearest" });
        follow.current = performance.now() < until ? requestAnimationFrame(step) : 0;
      };
      follow.current = requestAnimationFrame(step);
    }
    onToggle();
  };

  return (
    <div ref={ref} className="bg-white border border-[#ECECEC] rounded-[12px] overflow-hidden">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={d.isOpen}
        className="w-full flex justify-between items-center gap-[16px] px-[22px] py-[16px] cursor-pointer text-left hover:bg-[#FDFBFA]"
      >
        <div className="flex flex-col gap-[2px]">
          <span className="text-[15px] font-bold">{d.title}</span>
          <span className="text-[12px] text-[#6b6b6b]">{d.desc}</span>
        </div>
        {/* "+" whose vertical bar turns flat into the "−" when open. */}
        <span
          aria-hidden="true"
          className="relative w-[28px] h-[28px] flex-none rounded-[14px] border border-[#E8E0D9] flex items-center justify-center"
        >
          <span className="absolute w-[10px] h-[1.5px] rounded-[1px] bg-[#3a3a3a]" />
          <span
            className={`absolute w-[1.5px] h-[10px] rounded-[1px] bg-[#3a3a3a] motion-safe:transition-transform motion-safe:duration-300 ${
              d.isOpen ? "rotate-90" : ""
            }`}
          />
        </span>
      </button>
      {/* Kept mounted so closing can animate: the 0fr → 1fr row grows to the
          body's height. Hidden (out of tab order and the a11y tree) once closed. */}
      <div
        className="grid motion-safe:transition-[grid-template-rows] motion-safe:duration-300 motion-safe:ease-out"
        style={{ gridTemplateRows: d.isOpen ? "1fr" : "0fr" }}
      >
        <div
          className="min-h-0 overflow-hidden motion-safe:transition-[opacity,visibility] motion-safe:duration-300"
          style={{ opacity: d.isOpen ? 1 : 0, visibility: d.isOpen ? "visible" : "hidden" }}
        >
          <div className="px-[22px] pb-[22px] pt-[18px] border-t border-[#F1ECE8] flex flex-col gap-[14px]">
            <CompareWith tf={d.tf} onPick={onRange} />
            <div key={bodyKey} className={bodyClass}>
              {d.isBlock && <WellnessBlock block={d.block} />}
              {d.isTrend && <WellnessTrend trend={d.trend} size="sm" />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailSection;
